import React, {useState, useEffect, useCallback} from 'react';
import styled from 'styled-components';
import {
    AboveFoldScrollIndicator,
    Typography,
    Video,
    VerticalSpacer,
    Image,
    ImageProps,
    CmsButtonProps,
    Button
} from '../../elements/';
import {SectionInnerGridContent} from '../../elements/section/SectionInnerGridContent';
import {GridContainer} from '../../elements/grid/GridContainer';
import {EyebrowProps} from '../../elements/eyebrow/Eyebrow';
import {LocalVideoOptions} from '../../../types';
import {AnimateOnViewWrap} from '../../wraps/animation';
import {ScaleOnViewWrap} from '../../wraps/animation/ScaleOnViewWrap';
import {fadeIn, getScaling} from './ImmersizeHeader.scaling';
import {
    useBreakpointOnly,
    useBreakpointUp,
    useBreakpointDown,
    AccentColorOptions
} from 'epic-ue-theme';
import root from 'window-or-global';
import throttle from 'lodash.throttle';

const epicGamesHeaderHeight = 52;
const minScale = 0.6;
const videoRatio = 0.5625;

const Styled = styled.div<{maxVideoHeight?: number}>`
    .logo {
        min-height: 130px;

        img {
            object-fit: contain;
        }
    }

    .attribution-caption .paragraph {
        color: ${(p) => p.theme.palette.text.secondary};
        font-size: 10px;
        line-height: 16px;
        letter-spacing: 0.2px;
        text-align: center;
    }

    .buttons-container {
        display: flex;
        justify-content: center;
        flex-flow: row nowrap;
        ${(p) => p.theme.breakpoints.down('sm')} {
            flex-flow: row wrap;
        }
        & > span {
            padding-left: ${(p) => p.theme.spacing(2)}px;
            padding-right: ${(p) => p.theme.spacing(2)}px;
            ${(p) => p.theme.breakpoints.down('sm')} {
                padding-top: ${(p) => p.theme.spacing(2)}px;
            }
        }
    }

    &.overlay {
        .header-above-fold {
            width: 100vw;
            position: relative;
            overflow: hidden;
            height: calc(100vh - ${epicGamesHeaderHeight}px);
        }

        .video-size {
            height: calc(100vh - ${epicGamesHeaderHeight}px);
            opacity: 0;
            animation-name: ${fadeIn};
            animation-delay: 0s;
            animation-timing-function: ${(p) => p.theme.easing};
            animation-duration: 2s;
            animation-fill-mode: forwards;

            @media (prefers-reduced-motion: reduce) {
                animation: none;
                opacity: 1;
            }

            .video-container {
                margin-top: 56px; /* vertical spacer above the eyebrow */
            }
        }
    }

    &.stacked {
        /* modify the height in order to move up the content below the video when it is scaled up or down. Should be 72px of vertical margin */
        #header-video-scale {
            transition: height 1s ${(p) => p.theme.easing};
            height: ${(p) =>
                p.maxVideoHeight && p.maxVideoHeight > 0 ? `${p.maxVideoHeight + 72}px` : '100%'};
            &.min {
                transition-delay: 0.15s;
                height: ${(p) =>
                    p.maxVideoHeight && p.maxVideoHeight > 0
                        ? `${Math.floor(p.maxVideoHeight * minScale) + 72}px`
                        : '100%'};
            }
        }

        .video-ratio {
            padding-bottom: 56.25%;
            position: relative;
        }
        .video-size {
            position: absolute;
            top: 0;
            bottom: 0;
            left: 0;
            right: 0;
        }
        .logo > div {
            margin: 0 auto;
        }
    }
`;

const TitleGradient = styled.div`
    position: absolute;
    top: 0;
    width: 100%;

    ${(p) => p.theme.breakpoints.down('sm')} {
        height: 40vh;
        background: linear-gradient(
            180deg,
            ${(p) => p.theme.palette.background.default} 130px,
            /* 56 + 18 */ rgba(13, 16, 35, 0) 100%
        );
    }
    height: 35vh;
    background: linear-gradient(
        180deg,
        ${(p) => p.theme.palette.background.default} 74px,
        /* 56 + 18 */ rgba(13, 16, 35, 0) 100%
    );
`;

/**
 * overlay type will put the title on top of the video
 * stacked allows for a logo and the title above the video
 */
export type ImmersiveHeaderLayoutOptions = 'overlay' | 'stacked';
export interface ImmersiveHeaderProps {
    mediaOptions?: LocalVideoOptions;
    eyebrow?: EyebrowProps;
    title?: string;
    description?: string;
    caption?: string;
    primaryBtn?: CmsButtonProps;
    secondaryBtn?: CmsButtonProps;
    layout?: ImmersiveHeaderLayoutOptions;
    logo?: ImageProps;
    accentColor?: AccentColorOptions;
    forceVideoMobile?: boolean;
}

export const ImmersiveHeader = ({
    mediaOptions,
    eyebrow,
    title,
    description,
    caption,
    primaryBtn,
    secondaryBtn,
    layout = 'overlay',
    logo,
    accentColor,
    forceVideoMobile
}: ImmersiveHeaderProps): JSX.Element | null => {
    const isOverlay = layout === 'overlay';
    const isStacked = layout === 'stacked';

    const [maxVideoHeight, setMaxVideoHeight] = useState(0);

    const isLg = useBreakpointOnly('lg');
    const isXl = useBreakpointUp('xl');
    const isMd = useBreakpointDown('md');
    const scale = getScaling(isLg, isXl);

    const handleResize = useCallback(
        (e) => {
            let gridWidth = 0;
            if (root.document && root.document.body) {
                gridWidth = root.document.body.clientWidth;
            }
            const maxVideoHeight = Math.floor(gridWidth * videoRatio);
            setMaxVideoHeight(maxVideoHeight);
        },
        [setMaxVideoHeight, videoRatio]
    );

    const throttleResize = throttle(handleResize, 500, {leading: false});

    useEffect(() => {
        throttleResize();
        root.addEventListener('resize', throttleResize);
        return () => {
            root.removeEventListener('resize', throttleResize);
        };
    }, [throttleResize]);

    return (
        <Styled
            className={`ue-immersive-header ${isOverlay ? 'overlay' : 'stacked'}`}
            maxVideoHeight={maxVideoHeight}>
            <div className="header-above-fold">
                {isStacked ? (
                    <>
                        <GridContainer key="ue-immersive-header-stacked">
                            <SectionInnerGridContent>
                                <VerticalSpacer variant="custom" spacing={7} />
                                {logo ? (
                                    <AnimateOnViewWrap translateY={-10} duration={0.25}>
                                        <div className="logo">
                                            <Image {...logo} height={130} />
                                            <VerticalSpacer variant="heading" />
                                        </div>
                                    </AnimateOnViewWrap>
                                ) : (
                                    ''
                                )}
                                {title ? (
                                    <>
                                        <AnimateOnViewWrap
                                            delay={0.25}
                                            duration={0.5}
                                            translateY={-15}>
                                            <Typography variant="h1" as="h2">
                                                {title}
                                            </Typography>
                                        </AnimateOnViewWrap>
                                        <VerticalSpacer variant="custom" spacing={5} />
                                    </>
                                ) : (
                                    ''
                                )}
                            </SectionInnerGridContent>
                            <AboveFoldScrollIndicator translateY={0} />
                        </GridContainer>
                    </>
                ) : (
                    ''
                )}
                <ScaleOnViewWrap
                    id="header-video"
                    {...scale}
                    maxScale={1}
                    minScale={minScale}
                    disable={isOverlay}
                    disableFromBottom>
                    <div className="video-ratio">
                        <div className={`video-size`}>
                            <Video
                                key="ue-immersive-header-overlay-video"
                                mediaOptions={mediaOptions}
                                gradient={isOverlay}
                                forceVideoMobile={forceVideoMobile}>
                                {isOverlay ? (
                                    <GridContainer key="ue-immersive-header-overlay">
                                        <SectionInnerGridContent>
                                            <AnimateOnViewWrap translateY={-20} delay={0.5}>
                                                <VerticalSpacer variant="custom" spacing={7} />
                                                {eyebrow && eyebrow.text ? (
                                                    <>
                                                        {eyebrow && (
                                                            <Typography variant="h4" big as="h1">
                                                                {eyebrow.text}
                                                            </Typography>
                                                        )}
                                                        <VerticalSpacer variant="heading" />
                                                    </>
                                                ) : (
                                                    ''
                                                )}
                                                {title && (
                                                    <Typography variant="h1" as="h2">
                                                        {title}
                                                    </Typography>
                                                )}
                                            </AnimateOnViewWrap>
                                        </SectionInnerGridContent>
                                    </GridContainer>
                                ) : (
                                    ''
                                )}
                            </Video>
                            {isOverlay ? <TitleGradient className="title-gradient" /> : ''}
                        </div>
                    </div>
                </ScaleOnViewWrap>
            </div>

            {isOverlay ? <AboveFoldScrollIndicator /> : ''}

            {isMd ? <VerticalSpacer variant="custom" downSmSpacing={5} /> : ''}

            {description ? (
                <GridContainer key="ue-immersive-header-desc">
                    <SectionInnerGridContent>
                        <AnimateOnViewWrap translateY={30} duration={0.75}>
                            {isOverlay ? <VerticalSpacer variant="heading" /> : ''}
                            <Typography variant="h2">{description}</Typography>
                        </AnimateOnViewWrap>
                    </SectionInnerGridContent>
                </GridContainer>
            ) : (
                ''
            )}

            {caption ? (
                <div className="attribution-caption">
                    <VerticalSpacer variant="heading" />
                    <AnimateOnViewWrap translateY={30} duration={0.75}>
                        <Typography variant="p" caption>
                            {caption}
                        </Typography>
                    </AnimateOnViewWrap>
                </div>
            ) : (
                ''
            )}

            {(primaryBtn && primaryBtn.label) || (secondaryBtn && secondaryBtn.label) ? (
                <>
                    <VerticalSpacer variant="heading" />
                    <AnimateOnViewWrap translateY={30} duration={0.75}>
                        <div className="buttons-container">
                            {primaryBtn && primaryBtn.label ? (
                                <Button type="primary" href={primaryBtn.href} color={accentColor}>
                                    {primaryBtn.label}
                                </Button>
                            ) : (
                                ''
                            )}
                            {secondaryBtn && secondaryBtn.label ? (
                                <Button
                                    type="secondary"
                                    href={secondaryBtn.href}
                                    color={accentColor}>
                                    {secondaryBtn.label}
                                </Button>
                            ) : (
                                ''
                            )}
                        </div>
                    </AnimateOnViewWrap>
                </>
            ) : (
                ''
            )}
            <VerticalSpacer variant="section" />
        </Styled>
    );
};
